@if (contractLoading) {
<div class="loading-container">
    <mat-spinner diameter="28"></mat-spinner>
</div>
} @else if (notFound) {
<div class="not-found-container">
    <mat-icon>search_off</mat-icon>
    <h2>Contract not found</h2>
    <p>This sell contract link is invalid or no longer exists.</p>
</div>
} @else if (contract) {
<div class="contract-info-container">
    <div class="contract-info">
        <div class="contract-header">
            <h2 class="contract-name">{{ contract.name }}</h2>
            <div class="contract-chips">
                <span [class]="'status-chip ' + getContractStatusClass(contract.status)">{{ contract.status }}</span>
                <span [class]="'payment-chip ' + (contract.payment_status === 'Payed' ? 'payed' : 'not-payed')">
                    <mat-icon>{{ contract.payment_status === 'Payed' ? 'check_circle' : 'pending' }}</mat-icon>
                    {{ contract.payment_status === 'Payed' ? 'Paid' : 'Unpaid' }}
                </span>
            </div>
        </div>

        <div class="contract-stats">
            <div class="stat-item">
                <span class="stat-label">Target</span>
                <span class="stat-value">{{ contract.target_coins | number }}</span>
                <span class="stat-sub">{{ contract.target_type === 'sent' ? 'coins sent' : 'coins received' }}</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
                <span class="stat-label">Fee</span>
                <span class="stat-value">{{ contract.fee | number }}</span>
                <span class="stat-sub">Toman</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
                <span class="stat-label">Fee Amount</span>
                <span class="stat-value">{{ contract.fee_amount | number }}</span>
                <span class="stat-sub">coins</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
                <span class="stat-label">Est. Sent</span>
                <span class="stat-value">{{ contract.estimated_sent_coins | number }}</span>
                <span class="stat-sub">coins</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
                <span class="stat-label">Est. Received</span>
                <span class="stat-value">{{ contract.estimated_received_coins | number }}</span>
                <span class="stat-sub">coins</span>
            </div>
        </div>

        <div class="progress-section">
            <div class="progress-header">
                <span class="progress-label">Progress</span>
                <span class="progress-percent">{{ progressPercent }}%</span>
            </div>
            <div class="progress-track">
                <div class="progress-fill" [style.width.%]="progressPercent" [class.complete]="progressPercent >= 100">
                </div>
            </div>
        </div>

        <div class="contract-times">
            <div class="time-item">
                <mat-icon class="time-icon">play_circle_outline</mat-icon>
                <div class="time-content">
                    <span class="time-label">Started</span>
                    <span class="time-value">{{ formatDate(contract.started_at) }}</span>
                </div>
            </div>
            <div class="time-item">
                <mat-icon class="time-icon">timer</mat-icon>
                <div class="time-content">
                    <span class="time-label">Expires</span>
                    <span class="time-value">{{ formatDate(contract.expire_at) }}</span>
                </div>
            </div>
            @if (contract.finished_at) {
            <div class="time-item">
                <mat-icon class="time-icon">check_circle_outline</mat-icon>
                <div class="time-content">
                    <span class="time-label">Finished</span>
                    <span class="time-value">{{ formatDate(contract.finished_at) }}</span>
                </div>
            </div>
            }
        </div>

        @if (contract.current_issue) {
        <div class="alert issue-alert">
            <mat-icon>warning_amber</mat-icon>
            <div class="alert-body">
                <span class="alert-title">{{ formatIssueType(contract.current_issue.type) }}</span>
                <span class="alert-sub">Resumes at {{ formatDate(contract.current_issue.resume_at) }}</span>
            </div>
        </div>
        }

        @if (contract.failed_reason) {
        <div class="alert failed-alert">
            <mat-icon>error_outline</mat-icon>
            <div class="alert-body">
                <span class="alert-title">Failed</span>
                <span class="alert-sub">{{ contract.failed_reason }}</span>
            </div>
        </div>
        }

    </div>
</div>
}

<div class="cards-info-container">

    @if (loading) {
    <div class="loading-container">
        <mat-spinner diameter="36"></mat-spinner>
        <p>Loading cards...</p>
    </div>
    } @else {
    <div class="cards-header">
        <span>
            <div class="section-label">Cards</div>

            <div class="summary">
                <span>
                    Total: <strong>{{ cards.length }}</strong> cards &nbsp;·&nbsp;
                    Listed: <strong>{{ countByStatus('Listed') }}</strong> &nbsp;·&nbsp;
                    Sold: <strong>{{ countByStatus('Sold') }}</strong> &nbsp;·&nbsp;
                    Expired: <strong>{{ countByStatus('Expired') }}</strong> &nbsp;·&nbsp;
                    Quick Sold: <strong>{{ countByStatus('QuickSold') }}</strong> &nbsp;·&nbsp;
                    Left Behind: <strong>{{ countByStatus('LeftBehind') }}</strong>
                </span>

            </div>
        </span>

        <span class="spacer"></span>

        <button mat-icon-button (click)="load()" [disabled]="refreshing">
            @if (refreshing) {
            <mat-spinner diameter="18"></mat-spinner>
            } @else {
            <mat-icon>refresh</mat-icon>
            }
        </button>

    </div>

    <div class="players-list">
        @for (group of cardGroups; track group.key) {
        <mat-accordion class="player-accordion">
            <mat-expansion-panel class="player-card" [class]="group.items[0].status.toLowerCase()">
                <mat-expansion-panel-header>
                    <div class="player-header-expandable">
                        @if (group.items[0].player_image_path) {
                        <div class="player-thumb-wrapper" #thumbOrigin
                            (click)="showImagePreview(thumbOrigin, group.items[0].player_image_path); $event.stopPropagation()">
                            <img class="player-thumb" [src]="group.items[0].player_image_path"
                                [alt]="group.items[0].player_name" />
                        </div>
                        }
                        <div class="player-info">
                            <div class="player-title">
                                <span class="player-name">
                                    {{ group.items[0].player_name }}
                                    @if (group.items[0].player_nickname && group.items[0].player_nickname !==
                                    group.items[0].player_name)
                                    {
                                    ({{ group.items[0].player_nickname }})
                                    }
                                </span>
                            </div>
                            <div class="player-meta">
                                <span class="count-badge">×{{ group.count }}</span>
                                <span class="status">{{ group.items[0].status }}</span>
                            </div>
                        </div>

                        <div class="price-info">
                            <div class="price-row">
                                <span class="price-label">Start Bid:</span>
                                <span class="price-value">{{ group.items[0].bid_price | number }}</span>
                            </div>
                            <div class="price-row">
                                <span class="price-label">Buy Now:</span>
                                <span class="price-value">{{ group.items[0].buy_now_price| number }}</span>
                            </div>
                        </div>

                    </div>
                </mat-expansion-panel-header>

                <div class="expansion-content">
                    <div class="table-container">
                        <table mat-table [dataSource]="group.items" class="mat-elevation-z4">
                            <ng-container matColumnDef="player">
                                <th mat-header-cell *matHeaderCellDef>Player</th>
                                <td mat-cell *matCellDef="let c">
                                    <div class="player-cell">
                                        <span class="player-name">{{ c.player_name }}</span>
                                        @if (c.player_nickname && c.player_nickname !== c.player_name) {
                                        <span class="player-nick">({{ c.player_nickname }})</span>
                                        }
                                    </div>
                                </td>
                            </ng-container>

                            <ng-container matColumnDef="buy_now_price">
                                <th mat-header-cell *matHeaderCellDef>Buy Now</th>
                                <td mat-cell *matCellDef="let c">{{ formatCoins(c.buy_now_price) }}</td>
                            </ng-container>

                            <ng-container matColumnDef="cost">
                                <th mat-header-cell *matHeaderCellDef>Cost</th>
                                <td mat-cell *matCellDef="let c">{{ formatCoins(c.cost) }}</td>
                            </ng-container>

                            <ng-container matColumnDef="quality">
                                <th mat-header-cell *matHeaderCellDef>Quality</th>
                                <td mat-cell *matCellDef="let c">{{ c.quality }}</td>
                            </ng-container>

                            <ng-container matColumnDef="status">
                                <th mat-header-cell *matHeaderCellDef>Status</th>
                                <td mat-cell *matCellDef="let c">
                                    <span [class]="'status-badge ' + getCardStatusClass(c.status)">{{ c.status }}</span>
                                </td>
                            </ng-container>

                            <ng-container matColumnDef="expire_time">
                                <th mat-header-cell *matHeaderCellDef>Expires in</th>
                                <td mat-cell *matCellDef="let c">{{ formatTimeRemaining(c.expire_time) }}</td>
                            </ng-container>

                            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                            <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
                        </table>

                        @if (cards.length === 0) {
                        <div class="empty-state">No cards found for this contract.</div>
                        }
                    </div>
                </div>
            </mat-expansion-panel>
        </mat-accordion>
        }
    </div>
    }
</div>

<ng-template #imagePreviewTpl>
    <div (click)="hideImagePreview()">
        <img class="player-thumb-overlay" [src]="previewImageUrl" alt="Player preview" />
    </div>
</ng-template>